import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Icon from "../../../Icon";
import { classNames } from "../../../../utils/helpers";

const CodeVerification = (props) => {
  const {
    value,
    onChange,
    reset,
    isLoading,
    length = 6,
    error = "",
    disabled = false,
  } = props;

  const inputRefs = Array.from({ length }, () => useRef(null));

  const resetCode = () => {
    inputRefs.forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
    inputRefs[0].current.focus();
    onChange("");
  };

  useEffect(() => {
    if (reset) {
      resetCode();
    }
  }, [reset]);

  const _onChange = (newValue, index) => {
    const char = newValue.slice(-1);

    const newCodeArray = value.split("");
    newCodeArray[index] = char;
    const newCode = newCodeArray.join("");

    onChange(newCode);

    if (char && index < 5) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleKeyDown = (e, index) => {
    const input = e.target;
    const previousInput = inputRefs[index - 1];

    if ((e.key === "Backspace" || e.key === "Delete") && !input.value) {
      e.preventDefault();
      const newCodeArray = value.split("");
      newCodeArray[index] = "";
      const newCode = newCodeArray.join("");
      onChange(newCode);

      if (previousInput) {
        previousInput.current.focus();
      }
    }

    if (e.key === "ArrowLeft" && previousInput) {
      e.preventDefault();
      previousInput.current.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs[index + 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text").slice(0, 6);

    const newCodeArray = Array(6).fill("");

    for (let i = 0; i < pastedText.length; i++) {
      newCodeArray[i] = pastedText[i];
    }

    onChange(newCodeArray.join(""));

    const focusIndex = Math.min(pastedText.length - 1, 5);
    inputRefs[focusIndex]?.current?.focus();
  };

  return (
    <>
      <div className="px-4 py-2 flex justify-between gap-2">
        <div className="flex gap-2">
          {Array.from({ length }, (_, index) => (
            <input
              ref={inputRefs[index]}
              key={index}
              type="number"
              maxLength={1}
              autoFocus={index === 0}
              value={value[index] || ""}
              onChange={(e) => {
                _onChange(e.target.value, index);
              }}
              onPaste={handlePaste}
              disabled={isLoading || disabled}
              onFocus={handleFocus}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={classNames(
                `w-10 h-10 text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`,
              )}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={resetCode}
          className="text-3xl border-none rounded-lg h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-500"
          disabled={isLoading || disabled}
        >
          <Icon icon="delete" />
        </button>
      </div>
      <div className="flex justify-start">
        {error && <span className="text-red-500">{error}</span>}
      </div>
    </>
  );
};

CodeVerification.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  reset: PropTypes.bool,
  isLoading: PropTypes.bool,
  length: PropTypes.number,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

CodeVerification.defaultProps = {
  value: "",
  reset: false,
  isLoading: false,
  length: 6,
};

export default CodeVerification;
